/**
 * Transaction Sync Utilities
 * - Migration: add id + updatedAt to existing transactions
 * - Merge: combine transactions from two sources by id
 */

/**
 * Generate a unique ID for a transaction
 */
export const generateTxId = () => {
  // Use crypto.randomUUID if available, fallback to Math.random
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
};

/**
 * Migrate legacy transactions that don't have id or updatedAt fields.
 * This runs once on load and ensures all transactions have the required fields.
 * @param {Array} transactions - array of transaction objects
 * @returns {{ migrated: Array, wasMigrated: boolean }}
 */
export const migrateTransactions = (transactions) => {
  if (!Array.isArray(transactions)) return { migrated: [], wasMigrated: false };

  let wasMigrated = false;
  const seen = new Set();

  const migrated = transactions.map((tx, idx) => {
    let changed = false;
    const newTx = { ...tx };

    // Ensure every transaction has a unique id
    if (!newTx.id || seen.has(newTx.id)) {
      newTx.id = generateTxId();
      changed = true;
    }
    seen.add(newTx.id);

    // Ensure every transaction has updatedAt
    if (!newTx.updatedAt) {
      // Use createdAt if available, otherwise use a VERY OLD date so legacy local data 
      // never accidentally wins a merge against newer remote data.
      newTx.updatedAt = newTx.createdAt
        ? (typeof newTx.createdAt === 'number' ? new Date(newTx.createdAt).toISOString() : newTx.createdAt)
        : new Date(0).toISOString(); // 1970-01-01
      changed = true;
    }

    if (changed) wasMigrated = true;
    return newTx;
  });

  return { migrated, wasMigrated };
};

/**
 * Stamp a transaction with updatedAt = now.
 * Call this whenever a transaction is created or edited.
 */
export const stampTransaction = (tx) => ({
  ...tx,
  updatedAt: new Date().toISOString()
});

/**
 * Sort transactions by date (newest first), tie-broken by createdAt (newest first).
 * Shared by the real-time snapshot handler and optimistic local updates so the
 * ordering is always identical across code paths.
 */
export const sortTransactions = (arr) => {
  return [...(Array.isArray(arr) ? arr : [])].sort((a, b) => {
    const da = new Date(a.date || 0).getTime();
    const db = new Date(b.date || 0).getTime();
    if (db !== da) return db - da;
    const ca = typeof a.createdAt === 'number' ? a.createdAt : new Date(a.createdAt || 0).getTime();
    const cb = typeof b.createdAt === 'number' ? b.createdAt : new Date(b.createdAt || 0).getTime();
    return cb - ca;
  });
};

/**
 * Merge transactions from local and remote sources by ID.
 * - Transactions that exist only in one source are included as-is.
 * - Transactions that exist in both sources: the one with the LATEST updatedAt wins.
 * - Deleted transactions (exist in one but not the other): 
 *   We respect the deletion — if remote doesn't have it, it was deleted on another device.
 *   But only if remoteIsAuthoritative is true (meaning remote loaded successfully).
 *
 * @param {Array} localTx - local transactions (current state)
 * @param {Array} remoteTx - remote transactions (from Firestore)
 * @param {object} options
 * @param {boolean} options.remoteIsAuthoritative - if true, deletions in remote are respected
 * @returns {Array} merged transactions, sorted by date descending
 */
export const mergeTransactionsById = (localTx, remoteTx, { remoteIsAuthoritative = true } = {}) => {
  const localArr = Array.isArray(localTx) ? localTx : [];
  const remoteArr = Array.isArray(remoteTx) ? remoteTx : [];

  // Build maps by id
  const localMap = new Map();
  for (const tx of localArr) {
    if (tx.id) localMap.set(tx.id, tx);
  }

  const remoteMap = new Map();
  for (const tx of remoteArr) {
    if (tx.id) remoteMap.set(tx.id, tx);
  }

  const merged = new Map();

  // Add all remote transactions first (source of truth)
  for (const [id, tx] of remoteMap) {
    merged.set(id, tx);
  }

  // Merge local transactions
  for (const [id, localItem] of localMap) {
    const remoteItem = remoteMap.get(id);

    if (!remoteItem) {
      if (remoteIsAuthoritative) {
        // Remote doesn't have this tx — it was deleted on another device
        // Skip (respect the deletion)
        continue;
      } else {
        // Remote is not authoritative (e.g. fetch failed) — keep local
        merged.set(id, localItem);
      }
    } else {
      // Both have this tx — compare updatedAt, latest wins
      const localTime = new Date(localItem.updatedAt || 0).getTime();
      const remoteTime = new Date(remoteItem.updatedAt || 0).getTime();
      if (localTime > remoteTime) {
        merged.set(id, localItem);
      }
      // else: remote wins (already in merged)
    }
  }

  // Sort by date descending
  return [...merged.values()].sort((a, b) => {
    const da = new Date(a.date || 0).getTime();
    const db = new Date(b.date || 0).getTime();
    if (db !== da) return db - da;
    // Secondary sort by createdAt for same-date transactions
    const ca = typeof a.createdAt === 'number' ? a.createdAt : new Date(a.createdAt || 0).getTime();
    const cb = typeof b.createdAt === 'number' ? b.createdAt : new Date(b.createdAt || 0).getTime();
    return cb - ca;
  });
};
